import { Session } from 'src_common/sdk/session';
import { action, computed, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import {
    FilterCategoryType,
    getCategories,
    getCategoryId,
    hasCategoryMaxGames,
    sortByName,
    trendingCategoryName,
    casinoMiniGamesCategoryName,
} from 'src/domains/casino/utils/utils';
import {
    CategoriesAndGamesResponseType,
    CategoryType,
    CollectionType,
    InsertGameType,
    GameType,
} from 'src/domains/casino/models/models';

import {
    OpenapiCategoriesAndGamesStaffEditGameResponse200Type,
    ParamsType as EditGameParams,
} from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_game';
import {
    OpenapiCategoriesAndGamesStaffEditCategoryResponse200Type,
    ParamsType as EditCategoryParams,
} from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_edit_category';
import { OpenapiCategoriesAndGamesStaffDeleteCategoryResponse200Type } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_delete_category';
import { OpenapiCategoriesAndGamesStaffCreateCategoryResponse200Type } from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_create_category';
import {
    openapiCategoriesAndGamesStaffAddNewGameRequest,
    OpenapiCategoriesAndGamesStaffAddNewGameResponse200Type,
} from 'src/domains/casino/api/generated/categories-and-games/openapi_categories_and_games_staff_add_new_game';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { CasinoStoreAdapter } from 'src/domains/casino/utils/adapters';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

export interface SelectOptionsType {
    key: string;
    name: string;
}

type CategoryDetailsType = EditCategoryParams['requestBody']['category_details'];
export type LegacyEditGameParamsType = {
    gameId: EditGameParams['requestBody']['game_id'];
    gameDetails: EditGameParams['requestBody']['game_details'];
};

export class CategoriesAndGamesCollection {
    private readonly getCategoriesAndGames: Resource<CategoriesAndGamesResponseType>;
    private readonly casinoStoreAdapter: CasinoStoreAdapter;
    private readonly session: Session;
    public readonly casinoStoreEnabled: boolean;

    public constructor(
        public readonly collection: CollectionType,
        common: Common
    ) {
        this.session = common.session;
        this.casinoStoreEnabled = ConfigState.get(common).config.casinoStoreEnabled;
        this.casinoStoreAdapter = new CasinoStoreAdapter(common, this.casinoStoreEnabled);

        this.getCategoriesAndGames = new Resource(
            async (): Promise<CategoriesAndGamesResponseType> =>
                this.casinoStoreAdapter.getCategoriesAndGames(collection)
        );
        makeAutoObservable(this);
    }

    @computed private get categoriesAndGames(): CategoriesAndGamesResponseType | null {
        const result = this.getCategoriesAndGames.get();

        if (result.type === 'ready') {
            return result.value;
        }

        return null;
    }

    @computed public get categories(): Array<CategoryType> {
        if (this.categoriesAndGames !== null) {
            return this.categoriesAndGames.categories;
        }
        return [];
    }

    @computed public get games(): Array<GameType> {
        if (this.categoriesAndGames !== null) {
            return this.categoriesAndGames.games;
        }
        return [];
    }

    @computed public get categoryIds(): Array<number> {
        return this.categories.map((category) => category.id);
    }

    @computed public get publishedGamesBelongToActiveCategories(): Array<GameType> {
        const gameIds = this.getGameIdsFromCategories({ isActive: true });
        return this.games.filter((game) => gameIds.has(game.id) && game.published === true);
    }

    private getGameIdsFromCategories(filters: FilterCategoryType): Set<number> {
        const categories = getCategories(this.categories, filters);
        const gameIds: Set<number> = new Set();

        for (const category of categories) {
            for (const gameId of category.game_ids) {
                gameIds.add(gameId);
            }
        }
        return gameIds;
    }

    @action public refresh = async (): Promise<void> => {
        await this.getCategoriesAndGames.refresh();
    };

    @action public createCategory = async (
        position: number,
        name: string
    ): Promise<OpenapiCategoriesAndGamesStaffCreateCategoryResponse200Type> => {
        const response = await this.casinoStoreAdapter.createCategory(position, name, this.collection);
        await this.getCategoriesAndGames.refresh();
        return response;
    };

    @action public onRemoveCategory = async (
        categoryId: number
    ): Promise<OpenapiCategoriesAndGamesStaffDeleteCategoryResponse200Type> => {
        const response = await this.casinoStoreAdapter.removeCategory(categoryId, this.collection);
        await this.getCategoriesAndGames.refresh();
        return response;
    };

    @action public reorderCategories = async (
        newOrder: Array<number>,
        updatedCategory: { categoryId: number; newPosition: number }
    ): Promise<void> => {
        await this.casinoStoreAdapter.changeCategoryPosition(newOrder, updatedCategory, this.collection);
        await this.getCategoriesAndGames.refresh();
    };

    @action public editCategory = async (
        categoryId: number,
        categoryDetails: CategoryDetailsType
    ): Promise<OpenapiCategoriesAndGamesStaffEditCategoryResponse200Type> => {
        const response = await this.casinoStoreAdapter.editCategory(categoryId, categoryDetails, this.collection);
        await this.getCategoriesAndGames.refresh();
        return response;
    };

    @action public changeGamesOrder = async (
        categoryId: number,
        categoryDetails: CategoryDetailsType
    ): Promise<void> => {
        await this.casinoStoreAdapter.changeGamesOrder(categoryId, categoryDetails, this.collection);
        await this.getCategoriesAndGames.refresh();
    };

    @action public deleteGameFromCategory = async (gameId: number, categoryIds: Array<string>): Promise<void> => {
        await this.casinoStoreAdapter.deleteGameFromCategory(gameId, categoryIds, this.collection);
        await this.getCategoriesAndGames.refresh();
    };

    @action public addNewGame = async (
        gameDetails: InsertGameType
    ): Promise<OpenapiCategoriesAndGamesStaffAddNewGameResponse200Type> => {
        const response = await this.session.call(openapiCategoriesAndGamesStaffAddNewGameRequest, {
            requestBody: {
                collection: this.collection,
                game_details: gameDetails,
            },
        });
        await this.getCategoriesAndGames.refresh();
        return response;
    };

    @action public addGameToCategory = async (gameId: number, categoryId: string): Promise<void> => {
        await this.casinoStoreAdapter.assignGameToCategory(gameId, parseInt(categoryId, 10), 0);
        await this.getCategoriesAndGames.refresh();
    };

    @action public editGame = async (
        game: LegacyEditGameParamsType
    ): Promise<OpenapiCategoriesAndGamesStaffEditGameResponse200Type> => {
        const response = await this.casinoStoreAdapter.editGame(game);
        await this.getCategoriesAndGames.refresh();
        return response;
    };

    @action public deleteGame = async (gameId: number): Promise<void> => {
        await this.casinoStoreAdapter.deleteLiveGame(gameId);
        await this.getCategoriesAndGames.refresh();
    };

    public getCategoryPosition = (categoryId: number): number => {
        if (this.casinoStoreEnabled) {
            return this.categoryIds.indexOf(categoryId) + 1;
        }
        return this.categoryIds.indexOf(categoryId);
    };

    public getFixedCount = (): number => {
        return this.categories.filter((category) => category.fixed === true).length;
    };

    @computed public get trendingGamesCategoryId(): number | undefined {
        return getCategoryId(this.categories, trendingCategoryName);
    }

    @computed public get miniGamesCategoryId(): number | undefined {
        return getCategoryId(this.categories, casinoMiniGamesCategoryName);
    }

    @computed public get maxGamesInTrending(): boolean {
        return hasCategoryMaxGames(this.categories, this.trendingGamesCategoryId);
    }
}

export class GameStore {
    private readonly data: MobxMapAutoNew<CollectionType, CategoriesAndGamesCollection>;

    public constructor(common: Common) {
        this.data = new MobxMapAutoNew((collection: CollectionType) => {
            return new CategoriesAndGamesCollection(collection, common);
        });
        makeAutoObservable(this);
    }

    public getCollection(collection: CollectionType): CategoriesAndGamesCollection {
        return this.data.get(collection);
    }

    @computed public get liveCasinoCollection(): CategoriesAndGamesCollection {
        return this.data.get('live-casino');
    }

    @computed public get casinoCollection(): CategoriesAndGamesCollection {
        return this.data.get('casino');
    }

    @computed public get virtualsCollection(): CategoriesAndGamesCollection {
        return this.data.get('virtuals');
    }

    @computed public get liveCasinoGamesList(): Array<SelectOptionsType> {
        const gamesArray: Array<SelectOptionsType> = [];
        const liveCasinoGames = this.liveCasinoCollection.publishedGamesBelongToActiveCategories;

        for (const game of liveCasinoGames) {
            gamesArray.push({
                key: game.id.toString(),
                name: game.name,
            });
        }

        return gamesArray;
    }

    @computed public get casinoGamesList(): Array<SelectOptionsType> {
        const gamesArray: Array<SelectOptionsType> = [];
        const casinoGames = this.casinoCollection.publishedGamesBelongToActiveCategories;

        for (const game of casinoGames) {
            gamesArray.push({
                key: game.id.toString(),
                name: game.name,
            });
        }

        return gamesArray;
    }

    @computed public get virtualLeapGamesList(): Array<SelectOptionsType> {
        const gamesArray: Array<SelectOptionsType> = [];
        const virtualGames = this.virtualsCollection.publishedGamesBelongToActiveCategories;

        for (const game of virtualGames) {
            gamesArray.push({
                key: game.id.toString(),
                name: game.name,
            });
        }

        return gamesArray;
    }

    @computed public get gamesOptionsForPromos(): Array<SelectOptionsType> {
        const gamesMap: Map<string, SelectOptionsType> = new Map();
        for (const game of this.casinoGamesList) {
            gamesMap.set(game.key, game);
        }
        for (const game of this.liveCasinoGamesList) {
            gamesMap.set(game.key, game);
        }
        for (const game of this.virtualLeapGamesList) {
            gamesMap.set(game.key, game);
        }
        const gamesList = Array.from(gamesMap.values());
        return sortByName(gamesList);
    }
}
